/**
 * Sound Effects Manager
 * Uses Web Audio API to generate sounds programmatically (no external files needed)
 */

class SoundManager {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;

  constructor() {
    // Check if user has sound enabled in localStorage
    const soundPreference = localStorage.getItem('qorder-sound-enabled');
    this.enabled = soundPreference !== 'false'; // Enabled by default
  }

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  public toggle() {
    this.enabled = !this.enabled;
    localStorage.setItem('qorder-sound-enabled', String(this.enabled));
    return this.enabled;
  }

  public isEnabled() {
    return this.enabled;
  }

  // Success sound (major chord)
  public playSuccess() {
    if (!this.enabled) return;

    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Play a major chord (C-E-G)
    this.playTone(523.25, now, 0.15); // C5
    this.playTone(659.25, now + 0.05, 0.15); // E5
    this.playTone(783.99, now + 0.1, 0.3); // G5
  }

  // New order notification (ascending notes)
  public playNewOrder() {
    if (!this.enabled) return;

    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Ascending notification
    this.playTone(440, now, 0.1); // A4
    this.playTone(554.37, now + 0.1, 0.15); // C#5
    this.playTone(659.25, now + 0.2, 0.2); // E5
  }

  // Error/Warning sound
  public playError() {
    if (!this.enabled) return;

    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Dissonant notes
    this.playTone(200, now, 0.15);
    this.playTone(150, now + 0.15, 0.2);
  }

  // Click/Button sound
  public playClick() {
    if (!this.enabled) return;

    const ctx = this.getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.05);
  }

  // Order ready notification (bell-like)
  public playOrderReady() {
    if (!this.enabled) return;

    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Bell-like sound using multiple harmonics
    this.playTone(523.25, now, 0.3, 'sine', 0.15);
    this.playTone(1046.5, now, 0.2, 'sine', 0.1);
    this.playTone(1568, now, 0.15, 'sine', 0.05);
  }

  // Payment received (cash register)
  public playPaymentReceived() {
    if (!this.enabled) return;

    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    this.playTone(800, now, 0.05);
    this.playTone(1000, now + 0.05, 0.05);
    this.playTone(1200, now + 0.1, 0.1);
  }

  // Helper function to play a tone
  private playTone(
    frequency: number,
    startTime: number,
    duration: number,
    type: OscillatorType = 'sine',
    volume: number = 0.2
  ) {
    const ctx = this.getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;

    gainNode.gain.setValueAtTime(volume, startTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

    oscillator.start(startTime);
    oscillator.stop(startTime + duration);
  }
}

// Export singleton instance
export const soundManager = new SoundManager();

// Export individual functions for convenience
export const playSuccess = () => soundManager.playSuccess();
export const playNewOrder = () => soundManager.playNewOrder();
export const playError = () => soundManager.playError();
export const playClick = () => soundManager.playClick();
export const playOrderReady = () => soundManager.playOrderReady();
export const playPaymentReceived = () => soundManager.playPaymentReceived();
export const toggleSound = () => soundManager.toggle();
export const isSoundEnabled = () => soundManager.isEnabled();
